/* eslint-disable react/prop-types */
import {
  Card,
  Box,
  Grid,
  CardContent,
  Typography,
  Button,
  tooltipClasses,
  Zoom,
  IconButton,
  styled,
  Tooltip,
  Popper,
  Collapse,
} from '@mui/material';
import HelpTwoToneIcon from '@mui/icons-material/HelpTwoTone';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { calculosDelCredito } from '../../helper/liquidarCredito';
import { formateador } from '../../helper/Formateador';
import PrettoSlider from '../../client/components/calculadora/PrettoSlider';
import { hoy } from '../../helper/funtions';
import { solicitudCredito } from '../../store/clientUser/credito/thunk';

let bloqueo = false;

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} PopperComponent={Popper} TransitionComponent={Zoom} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

// Función para calcular la reactivación de plataforma según los días
const calcularReactivacionPlataforma = (dias) => {
  if (dias < 30) {
    return 0;
  }
  if (dias === 60) {
    return 79000;
  }
  if (dias === 90) {
    return 158000;
  }
  return 0;
};

const CalculadoraWeb = ({ showSolicitar = false }) => {
  const dispatch = useDispatch();
  const [valorSolicitado, setValorSolicitado] = useState(150000);
  const [tiempo, setTiempo] = useState(1);

  // Función para convertir días reales a valor del slider (0-100)
  const diasToSlider = (dias) => {
    if (dias <= 30) {
      // 1-30 días = 0-70 del slider
      return Math.round(((dias - 1) / 29) * 70);
    }
    if (dias === 60) {
      return 77.5; // Punto medio entre 70 y 85
    }
    return 92.5; // Punto medio entre 85 y 100
  };

  const [sliderValue, setSliderValue] = useState(() => diasToSlider(1)); // Valor interno del slider (0-100)

  // Función para convertir valor del slider (0-100) a días reales (para mostrar durante el arrastre)
  const sliderToDias = (sliderVal) => {
    if (sliderVal <= 70) {
      // 0-70 del slider = 1-30 días (más espacio)
      return Math.max(1, Math.min(30, Math.round(1 + (sliderVal / 70) * 29)));
    }
    // 70 corresponde a 30 días
    if (sliderVal === 70) {
      return 30;
    }
    // 77.5 corresponde a 60 días
    if (sliderVal === 77.5) {
      return 60;
    }
    // 92.5 corresponde a 90 días
    if (sliderVal === 92.5) {
      return 90;
    }
    // Para valores intermedios, determinar el más cercano
    if (sliderVal > 70 && sliderVal < 73.75) {
      return 30;
    }
    if (sliderVal >= 73.75 && sliderVal < 85) {
      return 60;
    }
    if (sliderVal >= 85) {
      return 90;
    }
    return 30; // Valor por defecto
  };

  const handlePrestamoChange = (_, valor) => {
    setValorSolicitado(Number(valor));
  };

  const handleTiempoPrestamoChange = (_, sliderVal) => {
    const nuevoSliderValue = Number(sliderVal);
    
    // Durante el arrastre, ajustar automáticamente a valores válidos cuando está cerca
    let valorAjustado = nuevoSliderValue;
    
    if (nuevoSliderValue <= 70) {
      // Rango 1-30: movimiento libre
      valorAjustado = nuevoSliderValue;
    } else if (nuevoSliderValue > 70 && nuevoSliderValue < 73.75) {
      // Cerca de 30 (70), ajustar a 30
      valorAjustado = 70;
    } else if (nuevoSliderValue >= 73.75 && nuevoSliderValue < 81.25) {
      // Cerca de 60 (77.5), ajustar a 60
      valorAjustado = 77.5;
    } else if (nuevoSliderValue >= 81.25 && nuevoSliderValue < 90) {
      // Cerca de 90 (92.5), ajustar a 90
      valorAjustado = 92.5;
    } else {
      // Más allá de 90
      valorAjustado = 92.5;
    }
    
    setSliderValue(valorAjustado);
    const diasReales = sliderToDias(valorAjustado);
    setTiempo(diasReales);
  };

  const handleTiempoPrestamoChangeCommitted = (_, sliderVal) => {
    // Cuando el usuario suelta el slider, asegurar que esté en un valor válido
    const nuevoSliderValue = Number(sliderVal);
    let valorAjustado = nuevoSliderValue;
    
    if (nuevoSliderValue <= 70) {
      valorAjustado = nuevoSliderValue;
    } else if (nuevoSliderValue > 70 && nuevoSliderValue < 73.75) {
      valorAjustado = 70; // 30 días
    } else if (nuevoSliderValue >= 73.75 && nuevoSliderValue < 81.25) {
      valorAjustado = 77.5; // 60 días
    } else if (nuevoSliderValue >= 81.25) {
      valorAjustado = 92.5; // 90 días
    }
    
    setSliderValue(valorAjustado);
    const diasReales = sliderToDias(valorAjustado);
    setTiempo(diasReales);
  };

  // Sincronizar sliderValue cuando tiempo cambia externamente
  useEffect(() => {
    const nuevoSliderValue = diasToSlider(tiempo);
    setSliderValue(nuevoSliderValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiempo]);

  const creditoConsultaCliente = {
    fecha_Desembolsado: moment(hoy).subtract(tiempo, 'days'),
    valor_Aprobado: valorSolicitado,
    fecha_Limite_Pago: moment(hoy).add(tiempo, 'days').format('YYYY-MM-DD'),
  };

  const { interesCorriente, valorPlataforma, valorAdministracion, valorIva } =
    calculosDelCredito(creditoConsultaCliente);

  // Calcular reactivación de plataforma
  const valorReactivacionPlataforma = calcularReactivacionPlataforma(tiempo);

  // Calcular total manualmente usando solo los valores que aplican para solicitudes nuevas
  // No incluimos: interesMoratorioFNH, primerCobranzaFNH, segundaCobranzaFNH
  // porque estos solo aplican cuando el crédito está vencido
  const valorTotalConReactivacion =
    Number(valorSolicitado) +
    Number(interesCorriente || 0) +
    Number(valorPlataforma || 0) +
    Number(valorAdministracion || 0) +
    Number(valorIva || 0) +
    valorReactivacionPlataforma;

  const submitSolicitudCredito = (e) => {
    e.preventDefault();
    dispatch(solicitudCredito(valorSolicitado, tiempo));
    bloqueo = true;
  };

  // Definir marcas para el slider de días con escala personalizada
  const marksDias = [
    { value: 0, label: '1' },
    { value: 70, label: '30' },
    { value: 77.5, label: '60' },
    { value: 92.5, label: '90' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card
          sx={{
            minWidth: { xs: '100%', sm: 275 },
            maxWidth: { xs: '100%', sm: 'none' },
            display: 'flex',
            justifyContent: 'center',
            boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.25)',
          }}
          variant="outlined"
        >
          <CardContent
            sx={{
              backgroundColor: '#0074bc',
            }}
          >
            <form onSubmit={submitSolicitudCredito}>
              <div>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    padding: { xs: '0.5em 1em', sm: '0.5em 2em' },
                    textAlign: 'center',
                    color: 'white',
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                  }}
                >
                  Solicita tu crédito
                </Typography>
              </div>

              <Box
                sx={{
                  paddingRight: { xs: '16px', sm: '48px' },
                  paddingLeft: { xs: '16px', sm: '31px' },
                }}
              >
                <PrettoSlider
                  aria-label="pretto slider"
                  defaultValue={150000}
                  min={150000}
                  valueLabelDisplay="on"
                  max={1000000}
                  onChange={handlePrestamoChange}
                  step={10000}
                  sx={{
                    display: 'flex',
                    width: { xs: '95%', sm: '90%' },
                    justifyContent: 'center',
                    marginLeft: { xs: '5px', sm: '15px' },
                    marginBottom: '10px',
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: { xs: '12px', sm: '15px' },
                  }}
                >
                  <Typography
                    sx={{
                      color: '#fff',
                      marginTop: '13px',
                      fontSize: { xs: '0.75rem', sm: '1rem' },
                    }}
                  >
                    MIN $150.000
                  </Typography>
                  <Typography
                    sx={{
                      color: '#fff',
                      marginTop: '13px',
                      fontSize: { xs: '0.75rem', sm: '1rem' },
                    }}
                  >
                    MAX $1.000.000
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    padding: { xs: '0.5em 1em', sm: '0.5em 2em' },
                    textAlign: 'center',
                    color: 'white',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  Días
                </Typography>

                <PrettoSlider
                  aria-label="pretto slider dias"
                  defaultValue={0}
                  value={sliderValue}
                  valueLabelDisplay="on"
                  valueLabelFormat={(value) => sliderToDias(value)}
                  min={0}
                  max={100}
                  marks={marksDias}
                  onChange={handleTiempoPrestamoChange}
                  onChangeCommitted={handleTiempoPrestamoChangeCommitted}
                  step={0.5}
                  sx={{
                    display: 'flex',
                    width: { xs: '95%', sm: '90%' },
                    justifyContent: 'center',
                    marginLeft: { xs: '5px', sm: '15px' },
                    marginBottom: { xs: '35px', sm: '40px' },
                    '& .MuiSlider-markLabel': {
                      color: '#fff',
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      top: { xs: '35px', sm: '40px' },
                      zIndex: 1,
                      pointerEvents: 'none',
                    },
                    '& .MuiSlider-thumb': {
                      zIndex: 2,
                    },
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    sx={{
                      color: '#fff',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      marginTop: '13px',
                    }}
                  >
                    1 Día
                  </Typography>
                  <Typography
                    sx={{
                      color: '#fff',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      marginTop: '13px',
                    }}
                  >
                    90 Días
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ marginBottom: '20px' }} />

              <Grid container>
                <Grid item xs={12} sm={12} md={12}>
                  <Box sx={{ margin: { xs: '0 16px', sm: '0 50px' } }}>
                    <Grid item xs={12} sm={12} md={12}>
                      <Typography
                        sx={{
                          marginBottom: '3px',
                          color: '#fff',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        CANTIDAD SOLICITADA DE PRESTAMO ($)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12}>
                      <Typography
                        sx={{
                          marginBottom: '20px',
                          backgroundColor: '#fff',
                          borderRadius: '10px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          color: 'black',
                          margin: '5px',
                          width: '100%',
                        }}
                      >
                        $ {formateador(valorSolicitado)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12}>
                      <Typography
                        sx={{
                          color: '#fff',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        DIAS SOLICITADOS DE PRESTAMO
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12}>
                      <Typography
                        sx={{
                          marginBottom: '20px',
                          backgroundColor: '#fff',
                          borderRadius: '10px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          color: 'black',
                          margin: '5px',
                          width: '100%',
                        }}
                      >
                        {tiempo} {tiempo === 1 ? 'Día' : 'Dias'}
                      </Typography>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ margin: { xs: '0 16px', sm: '0 50px' } }}>
                <Typography
                  sx={{
                    width: '100%',
                    color: '#fff',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  PLATAFORMA SERVICIOS (Opcional)
                  <HtmlTooltip
                    title=" El pago del servicio de plataforma te permite realizar cursos de Excel, cursos de finanzas, saber tu bienestar financiero, monitorear el estado de tu crédito cada día y realizar los pagos por cualquier medio como PSE entre otros. En caso de no tomar esta opción de la plataforma y deseas solo tomar un crédito y no los demás servicios puedes solicitar de forma física en nuestras oficinas y realizar los pagos de forma presencial en nuestras oficinas."
                    placement="right"
                    sx={{
                      maxWidth: 500,
                      fontSize: 30,
                    }}
                  >
                    <IconButton
                      aria-label="upload picture"
                      component="span"
                      sx={{
                        color: 'white',
                        padding: 0,
                        paddingLeft: '5px',
                      }}
                    >
                      <HelpTwoToneIcon />
                    </IconButton>
                  </HtmlTooltip>
                </Typography>
                <Typography
                  sx={{
                    marginBottom: '20px',
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: 'black',
                    margin: '5px',
                    width: '100%',
                  }}
                >
                  $ {formateador(valorPlataforma)}
                </Typography>
              </Box>

              <Box sx={{ margin: { xs: '0 16px', sm: '0 50px' } }}>
                <Typography
                  sx={{
                    textAlign: 'justify',
                    color: '#fff',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  INTERÉS ($) TASA 24% EA
                </Typography>
                <Typography
                  sx={{
                    marginBottom: '20px',
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: 'black',
                    margin: '5px',
                    width: '100%',
                  }}
                >
                  $ {interesCorriente ? formateador(interesCorriente) : 0}
                </Typography>
              </Box>

              <Box sx={{ margin: { xs: '0 16px', sm: '0 50px' } }}>
                <Typography
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    color: '#fff',
                  }}
                >
                  ESTUDIO FIRMA DIGITAL (Opcional)
                  <HtmlTooltip
                    title="El pago de este servicio se realiza la gestión de crédito como: estudio de crédito a través de centrales de riesgo y parámetros de easycredit, te permite tener un cupo disponible para cuando lo desees, permite tener respuesta y desembolso en 24 horas hábiles, te permite realizar firma electrónica. En caso de no tomar esta opción de la administración puedes presentar un fiador con propiedad raíz, puedes buscar un aval a favor de easycredit Colombia SAS quien garantice el pago del crédito. Esta documentación debe ser presentada en forma física."
                    placement="right"
                  >
                    <IconButton
                      aria-label="upload picture"
                      component="span"
                      sx={{
                        color: 'white',
                        padding: 0,
                        paddingLeft: '5px',
                      }}
                    >
                      <HelpTwoToneIcon />
                    </IconButton>
                  </HtmlTooltip>
                </Typography>
                <Typography
                  sx={{
                    marginBottom: '20px',
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: 'black',
                    margin: '5px',
                    width: '100%',
                  }}
                >
                  $ {formateador(valorAdministracion)}
                </Typography>
              </Box>

              <Box sx={{ margin: { xs: '0 16px', sm: '0 50px' } }}>
                <Typography
                  sx={{
                    lineHeight: '24px',
                    color: '#fff',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  IVA ($)
                </Typography>
                <Typography
                  sx={{
                    marginBottom: '20px',
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: 'black',
                    margin: '5px',
                    width: '100%',
                  }}
                >
                  $ {formateador(valorIva)}
                </Typography>
              </Box>

              <Collapse in={valorReactivacionPlataforma > 0} timeout={400}>
                <Box sx={{ margin: { xs: '0 16px', sm: '0 50px' } }}>
                  <Typography
                    sx={{
                      lineHeight: '24px',
                      color: '#fff',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    REACTIVACIÓN PLATAFORMA ($)
                  </Typography>
                  <Typography
                    sx={{
                      marginBottom: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '10px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      color: 'black',
                      margin: '5px',
                      width: '100%',
                    }}
                  >
                    $ {formateador(valorReactivacionPlataforma)}
                  </Typography>
                </Box>
              </Collapse>

              <Box sx={{ margin: { xs: '0 16px', sm: '0 50px' } }}>
                <Typography
                  sx={{
                    marginBottom: '3px',
                    color: '#fff',
                    width: '100%',
                    textAlign: 'justify',
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                  }}
                >
                  TOTAL A PAGAR ($)
                </Typography>
                <Typography
                  sx={{
                    marginBottom: '20px',
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: 'black',
                    margin: '5px',
                    width: '100%',
                  }}
                >
                  ${formateador(valorTotalConReactivacion)}
                </Typography>
              </Box>
              {showSolicitar && (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#FFDE2E',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    marginTop: '20px',
                    width: '100%',
                    color: 'black',
                    '&:hover': {
                      backgroundColor: '#FFFF99',
                    },
                  }}
                  disabled={bloqueo}
                >
                  {bloqueo ? 'Tienes una solicitud en proceso' : `¡SOLICITA YA!`}
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      </Grid>
    </Box>
  );
};

export default CalculadoraWeb;
